import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HelpCircle,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Calendar,
  Eye,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminSupportTickets } from '../../hooks/useAdminSupportTickets';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const STATUS_TABS = [
  { value: '', label: 'All', countKey: 'total' },
  { value: 'open', label: 'Open', countKey: 'open' },
  { value: 'in_progress', label: 'In Progress', countKey: 'in_progress' },
  { value: 'resolved', label: 'Resolved', countKey: 'resolved' },
  { value: 'closed', label: 'Closed', countKey: 'closed' },
];

const AdminSupportTickets = () => {
  const navigate = useNavigate();
  const {
    tickets,
    isLoading,
    error,
    filters,
    priorityOptions,
    categoryOptions,
    handleFilterChange,
    handleSearch,
    statistics: stats,
    getStatusColor,
    getPriorityColor
  } = useAdminSupportTickets();

  const tabCount = (key) => {
    if (key === 'total') return stats.total_tickets || 0;
    if (key === 'open') return stats.by_status?.open || 0;
    if (key === 'in_progress') return stats.by_status?.in_progress || 0;
    if (key === 'resolved') return stats.resolved_tickets || 0;
    if (key === 'closed') return stats.closed_tickets || 0;
    return 0;
  };

  const columns = [
    {
      key: 'id',
      label: 'Ticket ID',
      sortable: true,
      width: 'w-32',
      render: (ticket) => (
        <span className="font-mono text-sm font-medium text-primary-600 dark:text-primary-400">
          #{String(ticket.id).slice(0, 8)}
        </span>
      )
    },
    {
      key: 'user',
      label: 'User',
      sortable: true,
      width: 'w-48',
      render: (ticket) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
            {ticket.user?.name || 'N/A'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {ticket.user?.email || ''}
          </p>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      width: 'w-32',
      render: (ticket) => (
        <span className="text-sm text-gray-900 dark:text-gray-100 capitalize">
          {ticket.category || 'N/A'}
        </span>
      )
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      width: 'w-32',
      render: (ticket) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
          {ticket.priority ? ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1) : 'N/A'}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-32',
      render: (ticket) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
          {ticket.status ? ticket.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 'w-32',
      render: (ticket) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-50">
              {formatDate(ticket.created_at, 'medium')}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {formatTime(ticket.created_at)}
            </div>
          </div>
        </div>
      )
    }
  ];

  const actions = [
    {
      label: 'View & Reply',
      icon: Eye,
      variant: 'default',
      onClick: (ticket) => {
        navigate(`/admin/support-tickets/${ticket.id}`);
      }
    }
  ];

  if (error) {
    return (
      <div className="space-y-5">
        <AdminPageHeader
          title="Support Tickets"
          subtitle="Manage and respond to customer support tickets"
        />
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-white dark:bg-gray-900 p-6 text-center shadow-sm">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-50 mb-2">Error Loading Support Tickets</h3>
          <p className="text-gray-600 dark:text-gray-400">
            {error?.response?.data?.message || 'Failed to load support tickets. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Support Tickets"
        subtitle="Manage and respond to customer support tickets"
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.supportTickets.title}
        steps={ADMIN_PAGE_GUIDES.supportTickets.steps}
        icon={ListChecks}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Tickets</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">{stats.total_tickets || 0}</p>
            </div>
            <HelpCircle className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Open</p>
              <p className="text-2xl font-bold text-sky-600 dark:text-sky-400 mt-1 tabular-nums">{stats.open_tickets || 0}</p>
            </div>
            <Clock className="w-8 h-8 text-sky-500" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Resolved</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">{stats.resolved_tickets || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">High Priority</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1 tabular-nums">{stats.high_priority_tickets || 0}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-3 sm:px-5">
          <div
            className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
            role="tablist"
            aria-label="Ticket status"
          >
            {STATUS_TABS.map((tab) => {
              const isActive = filters.status === tab.value;
              return (
                <button
                  key={tab.value || 'all'}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => handleFilterChange('status', tab.value)}
                  className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {tab.label}{' '}
                  <span className="tabular-nums opacity-70">({tabCount(tab.countKey)})</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-5 space-y-5">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <SearchSelect
                label="Priority"
                value={filters.priority}
                onChange={(value) => handleFilterChange('priority', value)}
                options={priorityOptions}
              />
              <SearchSelect
                label="Category"
                value={filters.category}
                onChange={(value) => handleFilterChange('category', value)}
                options={categoryOptions}
              />
              <DatePicker
                label="From Date"
                value={filters.date_from}
                onChange={(value) => handleFilterChange('date_from', value)}
              />
              <DatePicker
                label="To Date"
                value={filters.date_to}
                onChange={(value) => handleFilterChange('date_to', value)}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading tickets…</p>
            </div>
          ) : (
            <DataTable
              data={tickets}
              columns={columns}
              actions={actions}
              isLoading={false}
              searchPlaceholder="Search by subject, description, user, or ticket number..."
              onSearch={handleSearch}
              emptyMessage="No support tickets found"
              className="!border-0 !shadow-none"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSupportTickets;
